import { useEffect, useState } from 'react'
import classNames from 'classnames'
import includes from 'lodash/includes'
import moment from 'moment'
import { useTranslation } from 'react-i18next'
import Layout from 'containers/Layout'
import LoadingIcon from 'components/atoms/LoadingIcon'
import apiGet from 'lib/network/apiGet'
import useGetParams from 'hooks/useGetParams'
import { TabReport } from './Sections'

// Keys must match BOOKING_FUNNEL_TABS in backend services/booking-funnel.service.ts.
const TABS = ['overview', 'supplyDemand', 'search', 'checkout', 'payment', 'newUsers', 'partner', 'office', 'cancellations', 'cars', 'segments']
const PRESETS = [7, 30, 90]
const PRIMARY = '#fe5579'

const daysAgo = n =>
  moment()
    .subtract(n - 1, 'days')
    .format('YYYY-MM-DD')
const today = () => moment().format('YYYY-MM-DD')

const BookingFunnel = () => {
  const { t, i18n } = useTranslation()
  const isRtl = i18n.language === 'ar'
  const [params, setParams] = useGetParams({ t: 'overview', from: daysAgo(30), to: today() }, false)
  const tab = includes(TABS, params.t) ? params.t : 'overview'
  const validRange = Boolean(params.from && params.to && params.from <= params.to)
  const [report, setReport] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!validRange) return undefined
    let cancelled = false
    setLoading(true)
    apiGet(`stats/funnel/${tab}`, { startDate: params.from, endDate: params.to })
      .then(res => !cancelled && setReport(res?.success ? res.data : null))
      .catch(() => !cancelled && setReport(null))
      .finally(() => !cancelled && setLoading(false))
    return () => {
      cancelled = true
    }
  }, [tab, params.from, params.to, validRange])

  const dateInput = 'text-sm bg-gray-100 border-0 rounded-sm px-2 py-1.5'

  return (
    <Layout
      title={t('bookingFunnel.title')}
      headerRight={
        <div className="flex flex-wrap items-center gap-2">
          {PRESETS.map(days => (
            <button
              key={days}
              type="button"
              onClick={() => setParams({ from: daysAgo(days), to: today() })}
              className="rounded-sm bg-gray-100 px-2 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-200"
            >
              {t('bookingFunnel.lastDays', { count: days })}
            </button>
          ))}
          <input type="date" className={dateInput} value={params.from || ''} max={params.to || undefined} onChange={e => setParams({ from: e.target.value })} />
          <input type="date" className={dateInput} value={params.to || ''} min={params.from || undefined} onChange={e => setParams({ to: e.target.value })} />
        </div>
      }
    >
      <div className="space-y-6 p-4 sm:p-6" dir={isRtl ? 'rtl' : 'ltr'}>
        <p className="max-w-3xl text-sm text-slate-500">{t('bookingFunnel.subtitle')}</p>

        <div className="flex gap-5 overflow-x-auto border-b border-slate-200">
          {TABS.map(key => (
            <button
              key={key}
              type="button"
              onClick={() => setParams({ t: key })}
              className={classNames(
                'relative shrink-0 whitespace-nowrap pb-3 text-sm font-medium transition',
                tab === key ? 'text-dark-700' : 'text-slate-400 hover:text-slate-600'
              )}
            >
              {t(`bookingFunnel.tabs.${key}`)}
              {tab === key && <span className="absolute inset-x-0 -bottom-px h-0.5 rounded-full" style={{ backgroundColor: PRIMARY }} />}
            </button>
          ))}
        </div>

        {!validRange ? (
          <p className="text-sm text-rose-600">{t('bookingFunnel.invalidRange')}</p>
        ) : loading ? (
          <div className="flex h-[260px] items-center justify-center">
            <LoadingIcon />
          </div>
        ) : report ? (
          <TabReport report={report} t={t} />
        ) : (
          <p className="py-10 text-center text-sm text-slate-400">{t('bookingFunnel.loadError')}</p>
        )}
      </div>
    </Layout>
  )
}

export default BookingFunnel
